import axios from 'axios';
import { User } from '../types';

// In production: use relative path (Caddy proxies /api/* to backend)
// In development: use absolute URL (dev server needs to reach backend:5000)
const API_URL = process.env.NODE_ENV === 'production'
    ? '/api'
    : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

// Register user
export const register = async (userData: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: string;
}): Promise<{ user: User; token: string }> => {
  try {
    const response = await axios.post(`${API_URL}/auth/register`, userData);

    if (response.data.success) {
      // Save token to localStorage
      localStorage.setItem('token', response.data.token);
      return response.data;
    }

    throw new Error(response.data.message || 'Registration failed');
  } catch (error: any) {
    // Handle axios errors properly
    if (error.response) {
      // Server responded with error status
      const serverError = error.response.data;
      if (serverError.errors && Array.isArray(serverError.errors)) {
        // Handle validation errors
        const errorMessage = serverError.errors.map((err: any) => err.msg).join(', ');
        throw new Error(`Registration failed: ${errorMessage}`);
      }
      throw new Error(serverError.message || 'Registration failed');
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('Unable to connect to server. Please check your connection.');
    } else {
      // Something else happened
      throw new Error(error.message || 'Registration failed');
    }
  }
};

// Login user
export const login = async (
  email: string,
  password: string
): Promise<{ user: User; token: string }> => {
  try {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });

    if (response.data.success) {
      // Save token to localStorage
      localStorage.setItem('token', response.data.token);
      return response.data;
    }

    throw new Error(response.data.message || 'Login failed');
  } catch (error: any) {
    // Handle axios errors properly
    if (error.response) {
      // Server responded with error status
      const serverError = error.response.data;
      if (serverError.errors && Array.isArray(serverError.errors)) {
        // Handle validation errors
        const errorMessage = serverError.errors.map((err: any) => err.msg).join(', ');
        throw new Error(`Login failed: ${errorMessage}`);
      }
      throw new Error(serverError.message || 'Login failed');
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('Unable to connect to server. Please check your connection.');
    } else {
      // Something else happened
      throw new Error(error.message || 'Login failed');
    }
  }
};

// Get user profile
export const getProfile = async (): Promise<User> => {
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await axios.get(`${API_URL}/auth/profile`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.data.success) {
    // Assuming the backend returns user data with firstName and lastName
    return response.data.user as User;
  }

  throw new Error(response.data.message || 'Failed to fetch profile');
};

// Logout user
export const logout = (): void => {
  localStorage.removeItem('token');
}; 